import { type DynamicWidgetIssue } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';

export type DynamicWidgetIssueControl =
    | 'retry_read'
    | 'show_source'
    | 'use_classic_view'
    | 'ask_phoebe';

export type DynamicWidgetErrorCatalogEntry = {
    readonly title: string;
    readonly body: string;
    readonly severity: DynamicWidgetIssue['level'];
    readonly allowedControls: ReadonlyArray<DynamicWidgetIssueControl>;
};

const LAST_VALID_AT_TOKEN = '{lastValidAt}';

export const DYNAMIC_WIDGET_ERROR_CATALOG = {
    source_invalid: {
        title: 'Some details are unavailable.',
        body: 'The rest of the card is still available.',
        severity: 'warning',
        allowedControls: ['show_source', 'ask_phoebe'],
    },
    source_missing: {
        title: 'Some details are unavailable.',
        body: 'The rest of the card is still available.',
        severity: 'warning',
        allowedControls: ['retry_read', 'show_source', 'ask_phoebe'],
    },
    source_stale: {
        title: 'This information may be out of date.',
        body: `Last valid update: ${LAST_VALID_AT_TOKEN}.`,
        severity: 'warning',
        allowedControls: ['retry_read', 'show_source', 'ask_phoebe'],
    },
    permission_denied: {
        title: 'You don’t have permission to do this.',
        body: 'Ask an administrator if you need access.',
        severity: 'error',
        allowedControls: ['show_source', 'ask_phoebe'],
    },
    action_rejected: {
        title: 'Phoebe couldn’t use the current selection.',
        body: 'Review the highlighted items.',
        severity: 'warning',
        allowedControls: ['show_source', 'ask_phoebe'],
    },
    action_failed: {
        title: 'This action didn’t finish.',
        body: 'No success was recorded.',
        severity: 'error',
        allowedControls: ['show_source', 'ask_phoebe'],
    },
    live_refresh_failed: {
        title: 'Couldn’t refresh this card.',
        body: `The last valid snapshot from ${LAST_VALID_AT_TOKEN} remains visible.`,
        severity: 'warning',
        allowedControls: ['retry_read', 'show_source', 'ask_phoebe'],
    },
    render_failed: {
        title: 'This card couldn’t be displayed.',
        body: 'The classic tool result is still available.',
        severity: 'error',
        allowedControls: ['use_classic_view', 'show_source', 'ask_phoebe'],
    },
    unsupported: {
        title: 'This action isn’t available here.',
        body: 'Use a supported action when one is available.',
        severity: 'info',
        allowedControls: [],
    },
} as const satisfies Record<
    DynamicWidgetIssue['code'],
    DynamicWidgetErrorCatalogEntry
>;

export function getDynamicWidgetErrorCatalogEntry(
    code: DynamicWidgetIssue['code'],
): DynamicWidgetErrorCatalogEntry {
    switch (code) {
        case 'source_invalid':
            return DYNAMIC_WIDGET_ERROR_CATALOG.source_invalid;
        case 'source_missing':
            return DYNAMIC_WIDGET_ERROR_CATALOG.source_missing;
        case 'source_stale':
            return DYNAMIC_WIDGET_ERROR_CATALOG.source_stale;
        case 'permission_denied':
            return DYNAMIC_WIDGET_ERROR_CATALOG.permission_denied;
        case 'action_rejected':
            return DYNAMIC_WIDGET_ERROR_CATALOG.action_rejected;
        case 'action_failed':
            return DYNAMIC_WIDGET_ERROR_CATALOG.action_failed;
        case 'live_refresh_failed':
            return DYNAMIC_WIDGET_ERROR_CATALOG.live_refresh_failed;
        case 'render_failed':
            return DYNAMIC_WIDGET_ERROR_CATALOG.render_failed;
        case 'unsupported':
            return DYNAMIC_WIDGET_ERROR_CATALOG.unsupported;
        default: {
            const exhaustiveCode: never = code;
            return exhaustiveCode;
        }
    }
}

export function getDynamicWidgetIssueBody(issue: DynamicWidgetIssue): string {
    const entry = getDynamicWidgetErrorCatalogEntry(issue.code);
    if (!entry.body.includes(LAST_VALID_AT_TOKEN)) {
        return entry.body;
    }
    const lastValidAt = issue.safeDetails['lastValidAt'];
    const renderedLastValidAt =
        typeof lastValidAt === 'string' && lastValidAt.length > 0
            ? lastValidAt
            : 'an earlier update';
    return entry.body.replace(LAST_VALID_AT_TOKEN, renderedLastValidAt);
}
